/**
 * Routing between the two commodity-heatmap render paths.
 *
 * Why this exists (#277): the commodity "predicted probability" and
 * "combined" heatmaps used to be rendered by feeding one point PER
 * PARCEL into leaflet.heat, which is a *density accumulator* — so
 * parcel density (i.e. settlement pattern), not geology, drove the
 * rendered color. In the Oregon gold belt that made the prediction
 * surface nearly anti-correlated with the actual USGS deposits: heat
 * piled up over the valley towns where parcels are dense and went cold
 * over the surrounding BLM mountains where the gold actually is.
 *
 * The server-side fix (#739) precomputes a parcel-INDEPENDENT ~1 km
 * grid in `commodity_heatmap_grid` (one cell = the median in-cell
 * probability, regardless of how many parcels fall in it) and serves
 * it as raster PNG tiles at
 *   /api/v1/tiles/commodity-heatmap/{z}/{x}/{y}.png?commodity=<c>
 * but the frontend was never switched over. This helper performs the
 * routing so the prediction/combined layers use the grid raster while
 * layers that are legitimately NOT parcel density stay on leaflet.heat:
 *   - '<c>-mines'    → real USGS MRDS deposit points (deposit density IS signal)
 *   - 'niobium*'     → no grid row exists for niobium
 *   - wildfire/flood* → NRI census-tract surfaces, not parcels
 */

// Commodities the scorer still writes a servable layer for. Narrower than
// COMMODITY_NAMES in backend tiles.py, which keeps all six so a replacement
// artifact can be tested without a backend change; the 72 hour freshness
// gate is what stops the retired ones being served.
export const GRID_COMMODITIES = ['gold', 'copper'];

/**
 * Prediction commodities withdrawn from the UI, with the reason. Two
 * distinct defects, both measured on the shipped v3.9 artifact:
 *
 * #1381 item 5, three heads that cannot produce a layer at all: two Platt
 * calibrators are inverted (the model scored inversely to its own labels,
 * so no cell can clear the threshold) and one artifact has no calibrator.
 *
 * #1468, silver: the opposite failure. Its calibrator is healthy and near
 * identical to copper's, but the grid supplies none of the geochem, fault
 * or radiometric columns these models were trained on, so ~60% of the
 * learned signal arrives as NaN and the booster falls through to a
 * constant. Silver's constant calibrates to 0.8936, above the 0.3
 * threshold, which is why it kept 95.50% of the country. That is not a
 * ranking, so there is nothing to render.
 *
 * The scorer stopped writing all four and the freshness gate in tiles.py
 * filters out what they last wrote.
 *
 * Mirrors RETIRED_COMMODITIES in scripts/score_grid_commodities.py, which
 * drops the same four from the request set so the chain step stops failing
 * on every run. Re-expose by moving a name back to GRID_COMMODITIES once
 * it has a head that loads and writes a non-degenerate field (#936 / #831).
 *
 * This only retires the *prediction* layers. '<c>-mines' stays: that is
 * real USGS MRDS deposit data and has nothing to do with the ML heads.
 */
export const RETIRED_COMMODITIES = {
  uranium: 'calibrator inverted (coef -3.2159), model is anti-predictive',
  lithium: 'no calibrator in the model metadata',
  ree: 'calibrator inverted (coef -2.4694), model is anti-predictive',
  silver: 'scores a featureless cell 0.8936, so 95.50% of the grid is kept '
    + 'as a constant rather than a ranking (#1468)',
};

/**
 * The commodity a prediction layer refers to: '-prob' (ML probability)
 * and the bare combined layer both name one, everything else (-mines,
 * -freq, -loss, hazard) does not.
 */
function predictionBase(layer) {
  if (!layer || typeof layer !== 'string') return null;
  return layer.endsWith('-prob') ? layer.slice(0, -'-prob'.length) : layer;
}

/**
 * Given a heatmap dropdown layer name, return the grid commodity to
 * render via the parcel-independent raster tiles, or null when the
 * layer should keep using the L.heatLayer point path.
 *
 * @param {string} layer e.g. 'gold-prob', 'gold', 'gold-mines', 'wildfire'
 * @returns {string|null} 'gold' | 'copper' | 'silver' | null
 */
export function gridCommodityForLayer(layer) {
  const base = predictionBase(layer);
  return base && GRID_COMMODITIES.includes(base) ? base : null;
}

/**
 * True when the layer is a retired *prediction* layer.
 *
 * Callers must suppress both render paths for these, not just the grid
 * one. Dropping a commodity from GRID_COMMODITIES alone makes
 * gridCommodityForLayer return null, and null is also how this module
 * says "render this via L.heatLayer", which for a prediction layer is
 * the per-parcel point path that made settlement density drive the
 * color (#277). A retired layer must render nothing at all, and an empty
 * map is the honest outcome; the density artifact is not.
 *
 * Reachable today even though the dropdown no longer offers these:
 * `heatmapLayer` is a persisted setting, so a selection made before the
 * retirement survives in sessionStorage and outlives a reload of the tab
 * it was made in.
 *
 * @param {string} layer
 * @returns {boolean}
 */
export function isRetiredCommodityLayer(layer) {
  const base = predictionBase(layer);
  return !!base && Object.prototype.hasOwnProperty.call(RETIRED_COMMODITIES, base);
}

/**
 * Build the raster tile URL template for react-leaflet's <TileLayer>.
 * `{z}/{x}/{y}` are left as Leaflet placeholders.
 *
 * @param {string} commodity one of GRID_COMMODITIES
 * @param {string} [apiBase] defaults to REACT_APP_API_URL || '/api/v1'
 */
export function commodityHeatmapTileUrl(commodity, apiBase) {
  const base = apiBase || process.env.REACT_APP_API_URL || '/api/v1';
  return `${base}/tiles/commodity-heatmap/{z}/{x}/{y}.png?commodity=${commodity}`;
}
